<script setup>

import { defineProps, defineEmits, computed, watch, ref} from "vue";
import BarraNavegacao from "./BarraNavegacao.vue";
import DadoConteudo from "./DadoConteudo.vue";
import UtilObject from "@/utils/UtilObject";
import UtilArray from "@/utils/UtilArray.mjs";

//==================================================================//
//========================= Props & Emits ==========================//
//==================================================================//

const props = defineProps([
    "usuarioStore",
    "colaboradorStore",
    "controlesDesativados",
    "baixandoDado",
    "dadoOriginal"
]);

const emits = defineEmits([
    "update:alteraControlesDesativados",
    "update:alteraBaixandoDado",
]);

//==================================================================//
//============================= Dado ===============================//
//==================================================================//

const dado = ref({});

//==================================================================//
//================= Altera Controles Desativados ===================//
//==================================================================//

const alteraControlesDesativados = (update)=>{

    emits('update:alteraControlesDesativados',update);

};

//==================================================================//
//===================== Altera Baixando Dado =======================//
//==================================================================//

const alteraBaixandoDado = (update = {}) =>{

    update.modal = false;
    emits('update:alteraBaixandoDado',update);

};

//==================================================================//
//===================== Altera Dado de Compra ======================//
//==================================================================//

const alteraDado = (update = {})=>{

    dado.value = UtilObject.merge(dado.value, update);

};

//==================================================================//
//==================== Altera Contas a Pagar =======================//
//==================================================================//

const alteraContasAPagar = (update = {})=>{

    let contasAPagar = dado?.value?.contasAPagar?.map((contaAPagar)=>{

        // Altera a Conta a Pagar com o valor do Update.
        contaAPagar.selecionado = update.selecionado;
        
        // Altera todos os Pagamentos da Conta a Pagar com o valor do Update.
        contaAPagar.pagamentos.forEach(pagamento =>{

            pagamento.selecionado = contaAPagar.selecionado;

        });

        return contaAPagar;

    });

    alteraDado({
        contasAPagar: contasAPagar,
        selecionado: update.selecionado,
    });

};

//==================================================================//
//================= Altera Conta a Pagar ===========================//
//==================================================================//

const alteraContaAPagar = (update = {})=>{

    let contasAPagar = dado?.value?.contasAPagar?.map((contaAPagar)=>{

        if(contaAPagar.id != update.id){
            return contaAPagar;
        }

        // Altera a Conta a Pagar especificada com o valor do Update.
        contaAPagar = UtilObject.merge(contaAPagar,update);

        // Altera todos os Pagamentos da Conta a Pagar com o valor do Selecionado do Update.
        contaAPagar.pagamentos.forEach(pagamento =>{

            pagamento.selecionado = contaAPagar.selecionado;

        });

        return contaAPagar;

    });

    let selecionado = contasAPagar?.every(({selecionado})=> selecionado == true);

    alteraDado({
        selecionado: selecionado,
        contasAPagar: contasAPagar
    });

};

//==================================================================//
//==================== Altera Pagamento ============================//
//==================================================================//

const alteraPagamento = (update = {})=>{

    let contasAPagar = dado?.value?.contasAPagar?.map((contaAPagar)=>{

        if(contaAPagar.id != update?.contaAPagar?.id){
            return contaAPagar;
        }

        // Altera o Pagamento selecionado da Conta a Pagar com o valor do Selecionado do Update.
        let pagamento = contaAPagar?.pagamentos?.find(({id})=> id === update?.pagamento?.id);
        pagamento.selecionado = update?.pagamento?.selecionado;

        // Altera a Conta a Pagar para Selecionada se todos os Pagamentos da Conta a Pagar foram selecionados.
        contaAPagar.selecionado = contaAPagar.pagamentos.every(({selecionado})=> selecionado == true);

        return contaAPagar;

    });

    let selecionado = contasAPagar?.every(({selecionado})=> selecionado == true);

    alteraDado({
        selecionado: selecionado,
        contasAPagar: contasAPagar
    });

};

//===================================================================//
//========================== Prepara Dado ===========================//
//=================================================-=================//

const preparaDado = (dado = {})=>{

    let contasAPagar = dado?.parcelas?.reduce((acc,parcela)=>{

        let contaAPagar = parcela?.contaAPagar;

        // Desconsidera todas as Contas a Pagar que não são Liberadas.
        // Desconsidera todas as Contas a Pagar que não são Boleto.
        // Desconsidera todas as Contas a Pagar que não tem Pagamentos.
        if(
            contaAPagar.status != "Liberada" ||
            contaAPagar.pagamentoForma != "Boleto" ||
            contaAPagar.pagamentosQuantidade <= 0
        ){
            return acc;
        }

        let pagamentos = contaAPagar?.pagamentos?.reduce((acc,pagamento)=>{

            // Desconsidera todos os Pagamentos que não são Pagamentos não Realizados
            if(pagamento?.status != "Pagamento não realizado"){
                return acc;
            }

            acc.push({
                id: pagamento?.id || "",
                selecionado: false,
                sequencia: pagamento?.sequencia || "",
                tipo: pagamento?.tipo || "",
                status: pagamento?.status || "",
                notaFiscalRecebidaID: pagamento?.notaFiscalRecebida?.id || "", 
                notaFiscalRecebidaNumero: pagamento?.notaFiscalRecebida?.numero || "",
                notaFiscalRecebidaChaveAcesso: pagamento?.notaFiscalRecebida?.chaveAcesso || "",
                notaFiscalRecebidaPlugNotasID: pagamento?.notaFiscalRecebida?.plugNotasID || "",
                dataCriacao: pagamento?.dataVencimentoFormatada || "",
                dataVencimento: pagamento?.dataVencimentoFormatada || "",
                valorDesconto: pagamento?.valorDesconto || 0,
                valorPagamento: pagamento?.valorPagamento || 0,
                valorReservado: pagamento?.valorReservado || 0,
                valorParaPagar: pagamento?.valorParaPagar || 0,
                valorPago: pagamento?.valorPago || 0,
                valorParaVincular: pagamento?.valorParaVincular || 0,
                valorVinculado: pagamento?.valorVinculado || 0,
            });

            return acc;

        },[]);

        // Desconsidera todas as Contas a Pagar que não tem Pagamentos para Realizar.
        if(UtilArray.isEmpty(pagamentos)){
            return acc;
        }

        acc.push({
            id: contaAPagar?.id || "",
            aberto: false,
            selecionado: false,
            codigo: contaAPagar?.codigo || "",
            parcelaCodigo: parcela?.codigo || "",
            status: contaAPagar?.status || "",
            forma: contaAPagar?.pagamentoForma || "",
            tipo: contaAPagar?.pagamentoTipo || "",
            evento: contaAPagar?.pagamentoEvento || "",
            descricao: contaAPagar?.descricao || "",
            dataVencimento: contaAPagar?.dataVencimentoFormatada || "",
            valorOriginal: contaAPagar?.valorOriginal || 0,
            valorParaReservar: contaAPagar?.valorParaReservar || 0,
            valorDesconto: contaAPagar?.valorDesconto || 0,
            valorPagamento: contaAPagar?.valorPagamento || 0,
            valorReservado: contaAPagar?.valorReservado || 0,
            valorParaPagar: contaAPagar?.valorParaPagar || 0,
            valorPago: contaAPagar?.valorPago || 0,
            valorParaVincular: contaAPagar?.valorParaVincular || 0,
            valorVinculado: contaAPagar?.valorVinculado || 0,
            liberadorNome: contaAPagar?.liberador?.nome || "",
            aprovadorNome: contaAPagar?.aprovador?.nome || "",
            reprovadorNome: contaAPagar?.reprovador?.nome || "",
            reprovadorJustificativa: contaAPagar?.reprovadorJustificativa || "",
            pagamentos: pagamentos || [],
            pagamentosQuantidade: contaAPagar?.pagamentosQuantidade || 0,
        });

        return acc;

    },[]);

    dado = {
        id: dado?.id,
        selecionado: false,
        tipo: dado?.tipo,
        codigo: dado?.codigo,
        contasAPagar: contasAPagar || []
    }

    alteraDado(dado);

};

//==================================================================//
//======================== Prepara Modal ===========================//
//==================================================================//

const preparaModal = ()=>{

    preparaDado(UtilObject.clone(props?.dadoOriginal));

}

//====================================================================//
//=== Prepara Modal se Dados Originais Carregados ou Recarregados ====//
//===================== Watch => baixandoDado ========================//
//====================================================================//

watch(()=> props?.baixandoDado, () => {

    if(props?.baixandoDado == true){
        preparaModal();
    }

},{ immediate: true });

</script>

<template>

    <main>

        <v-dialog
            content-class="c-dialog"
            v-model="props.baixandoDado"
            fullscreen
            transition="dialog-bottom-transition"
            persistent
        >

            <v-card class="c-modal">

                <BarraNavegacao
                    :usuarioStore="props.usuarioStore"
                    :colaboradorStore="props.colaboradorStore"
                    :controlesDesativados="controlesDesativados"
                    :dado="dado"
                    @update:alteraControlesDesativados="alteraControlesDesativados"
                    @update:alteraBaixandoDado="alteraBaixandoDado"
                ></BarraNavegacao>

                <DadoConteudo
                    :controlesDesativados="props.controlesDesativados"
                    :dado="dado"
                    @update:alteraContasAPagar="alteraContasAPagar"
                    @update:alteraContaAPagar="alteraContaAPagar"
                    @update:alteraPagamento="alteraPagamento"
                ></DadoConteudo>
            
            </v-card>

        </v-dialog>

    </main>

</template>

<style scoped>

.v-dialog__content >>> .v-dialog{
    background-color: white;
}

</style>

